import React, { useState, useEffect } from 'react';
import { CheckAuth } from 'auth/auth';
import { useGetDashboardInitQuery } from 'redux_store/services/client/compositeInit';
import {
  useGetPersonalDetailsQuery,
} from 'redux_store/services/client/profile';
import { useGetB2cDocumentListQuery } from 'redux_store/services/client/document';
import { useDropdowns } from 'hooks/useDropdowns';
import Loader from 'wrapper/loader';

const PREFETCH_TIMEOUT_MS = 10000; // 10 seconds timeout

const B2CDataPreloader = ({ children, applicationId: propApplicationId }) => {
  const [timedOut, setTimedOut] = useState(false);

  // Get applicationId from props or cached auth data
  const cachedUser = CheckAuth() || {};
  const applicationId =
    propApplicationId ||
    cachedUser.applications?.[0]?.applicationId ||
    cachedUser.pending_application_id ||
    cachedUser.application_id ||
    localStorage.getItem('applicationId') ||
    sessionStorage.getItem('applicationId');
  const userId = cachedUser.user_id;

  // Determine if we should skip queries that require applicationId
  const skip = !applicationId;

  // PRIORITY 1: Borrower profile (critical for all downstream views)
  const { isLoading: profileLoading, isUninitialized: profileUninitialized } =
    useGetPersonalDetailsQuery({ applicationId }, { skip });

  // PRIORITY 2: Dashboard init composite
  const { data: initData, isLoading: dashboardLoading, isUninitialized: dashboardUninitialized } =
    useGetDashboardInitQuery(undefined, { skip: false });

  // PRIORITY 3: Dropdown data (via unified hook)
  const { isLoading: dropdownLoading } =
    useDropdowns();

  // PRIORITY 4: Document list
  const { isLoading: docsLoading, isUninitialized: docsUninitialized } =
    useGetB2cDocumentListQuery({ applicationId }, { skip });

  // Dashboard readiness: initData resolved and pipeline has applications (so selectedAction will be set)
  const hasPipelineApps = (initData?.data?.pipeline?.applications ?? []).length > 0;
  const isDashboardReady = !dashboardLoading && !dashboardUninitialized && (hasPipelineApps || !!initData);

  // Consider loading if any query is loading or dashboard not ready
  const isLoading =
    dashboardLoading ||
    dashboardUninitialized ||
    dropdownLoading ||
    (!skip &&
      (profileLoading ||
        profileUninitialized ||
        docsLoading ||
        docsUninitialized)) ||
    !isDashboardReady;

  // Timeout: proceed after 10 seconds even if still loading
  useEffect(() => {
    if (!isLoading) return;

    const timer = setTimeout(() => {
      setTimedOut(true);
    }, PREFETCH_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isLoading]);

  // Show loading if still loading AND not timed out
  if (isLoading && !timedOut) {
    return <Loader variant="colorful" FullScreen={true} message="Preparing your dashboard..." subtitle="Loading your loan information" />;
  }

  return children;
};

export default B2CDataPreloader;
